"use client";
import React, { useRef } from "react";
import classNames from "classnames";

import Slider, { Settings } from "react-slick";

import styles from "./main.module.scss";
import "./externalStyles.scss";

export type RowData = {
  slides: number;
  rows: number;
};

export type SlidesPerRow = {
  slidesPerRow: RowData;
  superLargeDesktop: RowData;
  web: RowData;
  ipad: RowData;
  tablet?: RowData;
  mobile: RowData;
};
interface CarouselContainerProps {
  children: React.ReactNode;
  className?: string;
  slidesPerRowValues: SlidesPerRow;
  horizontalContent?: boolean;
}

const CarouselContainer = (props: CarouselContainerProps) => {
  const { children, className, slidesPerRowValues, horizontalContent = false } = props;
  const pointerDownX = useRef<number | null>(null);
  const dragged = useRef(false);

  const { superLargeDesktop, web, ipad, tablet, mobile } = slidesPerRowValues;

  const handlePointerDown = (e: React.PointerEvent) => {
    pointerDownX.current = e.clientX;
    dragged.current = false;
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (pointerDownX.current === null) return;
    if (Math.abs(e.clientX - pointerDownX.current) > 5) {
      dragged.current = true;
    }
  };

  const handlePointerUp = () => {
    pointerDownX.current = null;
  };

  const handleClickCapture = (e: React.MouseEvent) => {
    if (dragged.current) {
      e.stopPropagation();
      e.preventDefault();
    }
    dragged.current = false;
  };

  const settings: Settings = {
    accessibility: true,
    dots: true,
    infinite: false,
    speed: 500,
    initialSlide: 0,
    slidesToShow: web.slides,
    slidesToScroll: web.slides,
    rows: web.rows,
    responsive: [
      {
        breakpoint: 767,
        settings: {
          slidesToShow: mobile.slides,
          slidesToScroll: mobile.slides,
          rows: mobile.rows,
          arrows: false,
        },
      },
      ...(tablet
        ? [
            {
              breakpoint: 991,
              settings: {
                slidesToShow: tablet.slides,
                slidesToScroll: tablet.slides,
                rows: tablet.rows,
              },
            },
          ]
        : []),
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: ipad.slides,
          slidesToScroll: ipad.slides,
          rows: ipad.rows,
        },
      },
      {
        breakpoint: 1440,
        settings: {
          slidesToShow: web.slides,
          slidesToScroll: web.slides,
          rows: web.rows,
        },
      },
      {
        breakpoint: 6000,
        settings: {
          slidesToShow: superLargeDesktop.slides,
          slidesToScroll: superLargeDesktop.slides,
          rows: superLargeDesktop.rows,
        },
      },
    ],
  };

  return (
    <div
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onPointerLeave={handlePointerUp}
      onClickCapture={handleClickCapture}
    >
      <Slider
        {...settings}
        className={classNames(styles.sliderSlick, className, {
          [styles.horizontalContent]: horizontalContent,
        })}
      >
        {children}
      </Slider>
    </div>
  );
};

export default CarouselContainer;
